import { useCallback, useState } from "react";
import { useGo } from "@refinedev/core";
import { mfaSetupDismissed } from "@/utils/mfaSetupDismissed";
import { authService } from "services/authService/";
import { canUseMfa } from "pages/users/helpers";
import { useCompletedExperienceCount } from "./useCompletedExperienceCount";
import { useHasCompletedQuestionnaire } from "./useHasCompletedQuestionnaire";
import { useMfaFactors } from "./useMfaFactors";

// Drives the "Enable 2FA?" nudge shown once per user. Dismissing it —
// either via "Skip" or "Enable now" — marks it seen for good so it never
// nags again, even if the user abandons setup on /settings.
export const useMfaSetupNudge = (
  userId: string | number | undefined,
  isAuthenticated: boolean
) => {
  const go = useGo();
  const [dismissedThisSession, setDismissedThisSession] = useState(false);

  // A permanently-dismissed (or already-dismissed-this-session) nudge can
  // never open again, so there's no need to check MFA status, questionnaire
  // progress, or project count for it — skip all three requests entirely.
  const dismissed =
    dismissedThisSession ||
    (userId !== undefined && mfaSetupDismissed.isDismissed(userId));
  // SSO logins never check the MFA challenge (see canUseMfa), so nudging
  // those users to enable it would be a false sense of security.
  const shouldCheckEligibility =
    isAuthenticated &&
    userId !== undefined &&
    !dismissed &&
    canUseMfa(authService.getProvider());

  const { noFactorEnabled } = useMfaFactors(shouldCheckEligibility);

  // New accounts go through the questionnaire and welcome/first-project flow
  // before landing on a "real" home page. Both are their own async chains
  // racing against the mfa-factors fetch, so without this the nudge can
  // flash open in between and get covered by whichever onboarding screen
  // loads next. Hold it back until onboarding is fully done.
  const { isLoading: isLoadingQuestionnaire, hasCompletedQuestionnaire } =
    useHasCompletedQuestionnaire(shouldCheckEligibility);
  const { totalCount, isLoading: isLoadingProjects } =
    useCompletedExperienceCount(shouldCheckEligibility);
  const isOnboarding =
    isLoadingQuestionnaire ||
    !hasCompletedQuestionnaire ||
    isLoadingProjects ||
    totalCount === 0;

  const open = shouldCheckEligibility && noFactorEnabled && !isOnboarding;

  const dismiss = useCallback(() => {
    if (userId !== undefined) {
      mfaSetupDismissed.setDismissed(userId);
    }
    setDismissedThisSession(true);
  }, [userId]);

  const onEnableNow = useCallback(() => {
    dismiss();
    go({ to: "/settings" });
  }, [dismiss, go]);

  return { open, onEnableNow, onSkip: dismiss };
};
